import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye,
  ToggleLeft,
  ToggleRight,
  Clock,
  FileSignature,
  Copy,
  ArrowLeft,
  Layers,
  Grid3X3,
  List
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../../../components/ui';
import { PageHeader, InfoTooltip, ContextualHelp } from '../../../components/help';
import { contractsHelp } from '../../../config/helpContent';

type TemplateCategory = 'CESSAO' | 'CUSTODIA' | 'ADESAO' | 'DISTRIBUICAO' | 'GESTAO';

interface ContractTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    contracts: number;
  };
}

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  CESSAO: 'Cessão',
  CUSTODIA: 'Custódia',
  ADESAO: 'Adesão',
  DISTRIBUICAO: 'Distribuição',
  GESTAO: 'Gestão',
};

const CATEGORY_COLORS: Record<TemplateCategory, { bg: string; text: string }> = {
  CESSAO: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  CUSTODIA: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  ADESAO: { bg: 'bg-green-500/20', text: 'text-green-400' },
  DISTRIBUICAO: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  GESTAO: { bg: 'bg-surface-700', text: 'text-surface-300' },
};

export function TemplateList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const { data: templates, isLoading } = useQuery({
    queryKey: ['contract-templates', search, categoryFilter, activeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (activeFilter !== 'all') params.append('isActive', activeFilter === 'active' ? 'true' : 'false');
      
      const response = await api.get<ContractTemplate[]>(`/contracts/templates?${params}`);
      return response.data;
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await api.patch(`/contracts/templates/${id}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/contracts/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/contracts/templates/${id}/duplicate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
    },
  });

  const handleToggleActive = (template: ContractTemplate) => {
    toggleActiveMutation.mutate({ id: template.id, isActive: !template.isActive });
    setOpenMenu(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.')) {
      deleteMutation.mutate(id);
    }
    setOpenMenu(null);
  };

  const handleDuplicate = (id: string) => {
    duplicateMutation.mutate(id);
    setOpenMenu(null);
  };

  // Stats
  const stats = {
    total: templates?.length || 0,
    active: templates?.filter(t => t.isActive).length || 0,
    inactive: templates?.filter(t => !t.isActive).length || 0,
    totalContracts: templates?.reduce((acc, t) => acc + (t._count?.contracts || 0), 0) || 0,
  };

  return (
    <div className="min-h-screen p-6">
      {/* Back Button */}
      <div className="flex items-center gap-4 mb-2">
        <Link to="/admin/contracts" className="p-2 hover:bg-surface-800 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-surface-400" />
        </Link>
      </div>

      {/* Header with Help */}
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Templates de Contrato"
          description="Gerencie os modelos de contratos reutilizáveis. Use variáveis dinâmicas para personalização."
          icon={<Layers className="w-6 h-6 text-purple-400" />}
          module="Contratos Digitais"
          helpContent={contractsHelp.templates.helpContent}
        />
        <Link to="/admin/contracts/templates/new">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Template
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-primary-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-surface-100">{stats.total}</p>
                <div className="flex items-center gap-1">
                  <p className="text-sm text-surface-500">Total de Templates</p>
                  <InfoTooltip content="Número total de templates criados, incluindo ativos e inativos." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <ToggleRight className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-surface-100">{stats.active}</p>
                <div className="flex items-center gap-1">
                  <p className="text-sm text-surface-500">Ativos</p>
                  <InfoTooltip content="Templates disponíveis para uso na criação de novos contratos." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-surface-700 rounded-lg">
                <ToggleLeft className="w-5 h-5 text-surface-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-surface-100">{stats.inactive}</p>
                <div className="flex items-center gap-1">
                  <p className="text-sm text-surface-500">Inativos</p>
                  <InfoTooltip content="Templates desativados que não aparecem na criação de contratos." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface-800/50 border-surface-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <FileSignature className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-surface-100">{stats.totalContracts}</p>
                <div className="flex items-center gap-1">
                  <p className="text-sm text-surface-500">Contratos Gerados</p>
                  <InfoTooltip content="Total de contratos criados a partir destes templates." />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                <input
                  type="text"
                  placeholder="Buscar templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-surface-500" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
                className="px-3 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Todas categorias</option>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>

            <div className="flex items-center border border-surface-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2.5 transition-colors ${viewMode === 'table' ? 'bg-primary-500/20 text-primary-400' : 'bg-surface-800 text-surface-500 hover:bg-surface-700'}`}
                title="Visualização em tabela"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-primary-500/20 text-primary-400' : 'bg-surface-800 text-surface-500 hover:bg-surface-700'}`}
                title="Visualização em grade"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
            <p className="text-surface-400">Carregando templates...</p>
          </CardContent>
        </Card>
      ) : !templates || templates.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-surface-600" />
            <h3 className="text-lg font-medium text-surface-100 mb-2">Nenhum template encontrado</h3>
            <p className="text-surface-400 mb-6">
              {search || categoryFilter || activeFilter !== 'all' 
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando seu primeiro template de contrato'}
            </p>
            {!search && !categoryFilter && activeFilter === 'all' && (
              <Link to="/admin/contracts/templates/new">
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  Criar Template
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View */
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-800/50 border-b border-surface-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Versão
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Contratos
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Atualizado
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {templates.map((template) => (
                  <tr key={template.id} className="hover:bg-surface-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${CATEGORY_COLORS[template.category].bg}`}>
                          <FileText className={`w-5 h-5 ${CATEGORY_COLORS[template.category].text}`} />
                        </div>
                        <div>
                          <p className="font-medium text-surface-100">{template.name}</p>
                          <p className="text-xs text-surface-500">ID: {template.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${CATEGORY_COLORS[template.category].bg} ${CATEGORY_COLORS[template.category].text}`}>
                        {CATEGORY_LABELS[template.category]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-surface-300 font-mono text-sm">v{template.version}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-surface-300">{template._count?.contracts || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={template.isActive ? 'success' : 'default'}>
                        {template.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-surface-400">
                        <Clock className="w-4 h-4" />
                        {format(new Date(template.updatedAt), 'dd/MM/yyyy', { locale: ptBR })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenu(openMenu === template.id ? null : template.id)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-surface-400" />
                        </button>

                        {openMenu === template.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenMenu(null)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-surface-800 rounded-lg shadow-xl border border-surface-700 z-20">
                              <Link
                                to={`/admin/contracts/templates/${template.id}`}
                                className="flex items-center gap-2 px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                Visualizar
                              </Link>
                              <Link
                                to={`/admin/contracts/templates/${template.id}/edit`}
                                className="flex items-center gap-2 px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                                Editar
                              </Link>
                              <button
                                onClick={() => handleDuplicate(template.id)}
                                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                              >
                                <Copy className="w-4 h-4" />
                                Duplicar
                              </button>
                              <button
                                onClick={() => handleToggleActive(template)}
                                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-surface-200 hover:bg-surface-700 transition-colors"
                              >
                                {template.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4" />
                                    Ativar
                                  </>
                                )}
                              </button>
                              <div className="border-t border-surface-700 my-1" />
                              <button
                                onClick={() => handleDelete(template.id)}
                                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Excluir
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:border-surface-600 transition-all">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-lg ${CATEGORY_COLORS[template.category].bg}`}>
                      <FileText className={`w-5 h-5 ${CATEGORY_COLORS[template.category].text}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-surface-100 line-clamp-1">{template.name}</h3>
                      <span className={`text-xs font-medium ${CATEGORY_COLORS[template.category].text}`}>
                        {CATEGORY_LABELS[template.category]}
                      </span>
                    </div>
                  </div>
                  <Badge variant={template.isActive ? 'success' : 'default'} className="text-xs">
                    {template.isActive ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                <div className="flex items-center gap-4 text-sm text-surface-400 mb-4">
                  <span className="flex items-center gap-1">
                    <FileSignature className="w-4 h-4" />
                    {template._count?.contracts || 0} contratos
                  </span>
                  <span className="font-mono">v{template.version}</span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-surface-700">
                  <span className="text-xs text-surface-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {format(new Date(template.updatedAt), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/contracts/templates/${template.id}`}>
                      <Button variant="ghost" size="sm" className="p-2">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to={`/admin/contracts/templates/${template.id}/edit`}>
                      <Button variant="ghost" size="sm" className="p-2">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDelete(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Contextual Help FAQs */}
      <ContextualHelp faqs={contractsHelp.faqs} />
    </div>
  );
}
